import React, { useState, useRef } from "react";
import { Container, Typography, Box, Card, CardContent, Modal, IconButton } from "@mui/material";
import { motion } from "framer-motion";
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { useUIContext } from '../context/UIContext';

const VideoDemos = () => {
    const { viewport } = useUIContext();
    const [expandedVideo, setExpandedVideo] = useState(null);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const swiperRef = useRef(null);

    const getSlidesPerView = () => {
        if (viewport === 'mobile') return 1;
        if (viewport === 'tablet') return 2;
        return 3;
    };

    const handleExpandVideo = (video) => {
        setExpandedVideo(video);
        setIsVideoPlaying(true);
        if (swiperRef.current && swiperRef.current.swiper) {
            swiperRef.current.swiper.autoplay.stop();
        }
    };

    const handleCloseExpanded = () => {
        setExpandedVideo(null);
        setIsVideoPlaying(false);
        if (swiperRef.current && swiperRef.current.swiper) {
            swiperRef.current.swiper.autoplay.start();
        }
    };

    const handleVideoInteraction = (isPlaying) => {
        setIsVideoPlaying(isPlaying);
        if (swiperRef.current && swiperRef.current.swiper) {
            if (isPlaying) {
                swiperRef.current.swiper.autoplay.stop();
            } else {
                swiperRef.current.swiper.autoplay.start();
            }
        }
    };

    const videoData = [
        {
            title: "Dashboard Walkthrough",
            description: "Get a comprehensive overview of the NasRadius dashboard and its key features.",
            vimeoId: "1139271124",
        },
        {
            title: "Add Customer",
            description: "Learn how to quickly add and manage customers in your system.",
            vimeoId: "1139270894",
        },
        {
            title: "Payments and Transactions",
            description: "See how to process payments and track transactions seamlessly.",
            vimeoId: "1139271229",
        },
        {
            title: "Plan Setup",
            description: "Discover how to create and configure internet plans for your customers.",
            vimeoId: "1139271318",
        },
        {
            title: "Assign Coupon and Voucher",
            description: "Learn how to generate and assign coupons and vouchers to customers.",
            vimeoId: "1139271040",
        },
        {
            title: "Settings and Mikrotiks",
            description: "Explore settings configuration and MikroTik router management features.",
            vimeoId: "1139271784",
        }
    ];

    const getVimeoEmbedUrl = (vimeoId) => {
        return `https://player.vimeo.com/video/${vimeoId}?title=0&byline=0&portrait=0&responsive=1`;
    };

    return (
        <Box
            sx={{
                backgroundColor: '#ffffff',
                py: { xs: 10, md: 18 },
            }}
            id="video-demos"
        >
            <Container maxWidth="lg">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
                >
                    <Typography
                        variant="h2"
                        align="center"
                        sx={{
                            fontWeight: 700,
                            letterSpacing: '-0.03em',
                            background: 'linear-gradient(135deg, #19b594 0%, #0b74c1 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            mb: 2,
                        }}
                    >
                        Watch Our Demo Videos
                    </Typography>
                    <Typography
                        sx={{
                            textAlign: 'center',
                            color: 'rgba(29,29,31,0.65)',
                            fontSize: 'clamp(1.05rem, 0.3vw + 0.5rem, 1.25rem)',
                            maxWidth: '600px',
                            mx: 'auto',
                            mb: { xs: 6, md: 10 },
                            lineHeight: 1.6,
                        }}
                    >
                        Learn how to use NasRadius with our step-by-step video tutorials covering all major features.
                    </Typography>
                </motion.div>

                <Swiper
                    ref={swiperRef}
                    spaceBetween={24}
                    slidesPerView={getSlidesPerView()}
                    loop={true}
                    pagination={{ clickable: true }}
                    autoplay={{
                        delay: 3000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true,
                    }}
                    modules={[Pagination, Autoplay]}
                    onSlideChange={() => {
                        if (isVideoPlaying && swiperRef.current && swiperRef.current.swiper) {
                            swiperRef.current.swiper.autoplay.stop();
                        }
                    }}
                    style={{
                        paddingBottom: '48px',
                    }}
                >
                    {videoData.map((video, index) => (
                        <SwiperSlide key={index}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 0.5,
                                    delay: index * 0.05,
                                    ease: [0.25, 0.1, 0.25, 1],
                                }}
                            >
                                <Card
                                    sx={{
                                        borderRadius: 4,
                                        boxShadow: 'none',
                                        border: '1px solid rgba(0,0,0,0.06)',
                                        transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                                        overflow: 'hidden',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: '0 20px 48px rgba(0,0,0,0.1)',
                                        },
                                    }}
                                    onClick={() => handleExpandVideo(video)}
                                >
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            paddingTop: '56.25%',
                                            backgroundColor: '#000',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <iframe
                                            src={getVimeoEmbedUrl(video.vimeoId)}
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                border: 'none',
                                                pointerEvents: 'none',
                                            }}
                                            allow="autoplay; fullscreen; picture-in-picture"
                                            allowFullScreen
                                            title={video.title}
                                        />
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 12,
                                                right: 12,
                                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                borderRadius: '50%',
                                                p: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                transition: 'background-color 0.3s ease',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                },
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleExpandVideo(video);
                                            }}
                                        >
                                            <FullscreenIcon sx={{ color: '#fff', fontSize: '18px' }} />
                                        </Box>
                                    </Box>
                                    <CardContent
                                        sx={{
                                            p: 3,
                                            backgroundColor: '#fff',
                                            flexGrow: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <Typography
                                            variant="h5"
                                            sx={{
                                                fontWeight: 600,
                                                color: '#1d1d1f',
                                                letterSpacing: '-0.015em',
                                                fontSize: { xs: '1.15rem', md: '1.35rem' },
                                                mb: 1,
                                            }}
                                        >
                                            {video.title}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontWeight: 400,
                                                color: 'rgba(29,29,31,0.65)',
                                                fontSize: { xs: '0.9rem', md: '0.95rem' },
                                                lineHeight: 1.6,
                                            }}
                                        >
                                            {video.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        </SwiperSlide>
                    ))}
                </Swiper>

                <Modal
                    open={Boolean(expandedVideo)}
                    onClose={handleCloseExpanded}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: { xs: 2, md: 4 },
                    }}
                    onClick={handleCloseExpanded}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            width: { xs: '100%', md: '90%', lg: '80%' },
                            maxWidth: '1200px',
                            backgroundColor: '#000',
                            borderRadius: 3,
                            overflow: 'hidden',
                            outline: 'none',
                            boxShadow: '0 40px 80px rgba(0,0,0,0.5)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <IconButton
                            onClick={handleCloseExpanded}
                            sx={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                color: '#fff',
                                zIndex: 1000,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>

                        {expandedVideo && (
                            <>
                                <Box
                                    sx={{
                                        position: 'relative',
                                        paddingTop: '56.25%',
                                        width: '100%',
                                    }}
                                >
                                    <iframe
                                        src={getVimeoEmbedUrl(expandedVideo.vimeoId) + '&autoplay=1'}
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            border: 'none',
                                        }}
                                        allow="autoplay; fullscreen; picture-in-picture"
                                        allowFullScreen
                                        title={expandedVideo.title}
                                        onLoad={() => handleVideoInteraction(true)}
                                    />
                                </Box>
                                <Box sx={{ p: 3, backgroundColor: '#fff' }}>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#1d1d1f',
                                            letterSpacing: '-0.015em',
                                            mb: 0.5,
                                        }}
                                    >
                                        {expandedVideo.title}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: 'rgba(29,29,31,0.65)',
                                            lineHeight: 1.6,
                                            fontSize: '0.95rem',
                                        }}
                                    >
                                        {expandedVideo.description}
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Box>
                </Modal>
            </Container>
        </Box>
    );
};

export default VideoDemos;
